import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project } from '../../../utils/models';

import Conditional from '../../common/conditional';
import Confirm from '../../themed/confirm';
import CustomCover from '../../custom-reports/custom-cover';
import Header from '../../blocks/header';
import ImportProject from '../../forms/importProject';
import StepIndicator from '../../themed/step-indicator';
import UploadReportPDF from '../../forms/uploadReportPDF';

import classNames from './style.module.scss';
import routes from '../../../utils/routes';

const ReportUpload: React.FC = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [project, setProject] = useState<Project | null>(null);
    const [coverUrl, setCoverUrl] = useState<string>('');

    const navigate = useNavigate();

    const handleOnCreateProject = useCallback((createdProject: Project) => {
        setProject(createdProject);
        setCurrentStep(2);
    }, []);

    const handleGenerateCoverURL = useCallback((url: string) => {
        setCoverUrl(url);
        setCurrentStep(3);
    }, []);

    const handleReportUpload = useCallback(() => {
        setCurrentStep(4);
    }, []);

    const handleResetData = useCallback(() => {
        setCurrentStep(1);
        setProject(null);
    }, []);

    const steps = useMemo(() => {
        return [
            {
                step: 1,
                label: 'Import Project',
                Component: <ImportProject onCreateProject={handleOnCreateProject} />,
                dependency: project === null,
            },
            {
                step: 2,
                label: 'Confirm Report Cover Page',
                Component: project ? <CustomCover project={project} onGenerateURL={handleGenerateCoverURL} /> : <></>,
                dependency: project !== null,
            },
            {
                step: 3,
                label: 'Upload Report',
                Component: <UploadReportPDF project={project} onUploadReport={handleReportUpload} />,
                dependency: Boolean(coverUrl && project),
            },
            {
                step: 4,
                label: 'Confirm Report Upload',
                Component: <Confirm  title="Success!" message="Would you like to upload another report?" onCancel={() => navigate(routes.projects.fullPath)} onConfirm={handleResetData} />,
                dependency: Boolean(coverUrl && project),
            },
        ];
    }, [handleOnCreateProject, handleReportUpload, handleResetData, navigate, handleGenerateCoverURL, coverUrl, project]);

    return ( 
        <>
            <Header subtitle="Upload Report" buttonIcon="file-earmark-plus-fill" />

            <div className="m-5">
                 <div className={classNames.reportUpload}>
                    <StepIndicator
                        progress={Math.ceil((currentStep / steps.length) * 100)}
                        totalSteps={steps.length}
                        activeStep={currentStep}
                    />
                </div>

                <div className="mt-3">
                    {steps.map((step) => (
                        <Conditional key={step.step} isVisible={step.dependency && currentStep === step.step}>
                            <div className={classNames.reportUpload}>
                                {step.Component}
                            </div>
                        </Conditional>
                    ))}
                </div>
            </div>
        </>
     );
};

 
export default ReportUpload;